import { get, post } from '@/libs/http';
import { createApiQuery } from '@/hooks/use-api-query';
import { createApiMutationHook } from '@/hooks/use-api-mutation';
import type {
  UserApiAuthGoogleSigninReq,
  UserApiAuthGoogleSigninRes,
} from '@/types/api/third-party-login/-user-api-auth-google-signin.d';
import type {
  UserApiAuthFacebookSigninReq,
  UserApiAuthFacebookSigninRes,
} from '@/types/api/third-party-login/-user-api-auth-facebook-signin.d';
import type {
  UserApiAuthEthereumNonceReq,
  UserApiAuthEthereumNonceRes,
} from '@/types/api/third-party-login/-user-api-auth-ethereum-nonce.d';
import type {
  UserApiAuthSessionReq,
  UserApiAuthSessionRes,
} from '@/types/api/third-party-login/-user-api-auth-session.d';
import type {
  UserApiAuthSignoutReq,
  UserApiAuthSignoutRes,
} from '@/types/api/third-party-login/-user-api-auth-signout.d';
import type {
  UserApiAuthCredentialsCallbackReq,
  UserApiAuthCredentialsCallbackRes,
} from '@/types/api/third-party-login/-user-api-auth-credentials-callback.d';
import type {
  UserApiAuthTelegramCallbackReq,
  UserApiAuthTelegramCallbackRes,
} from '@/types/api/third-party-login/-user-api-auth-telegram-callback.d';

/**
 * 获取 Google OAuth2 授权链接
 */
export const getGoogleSigninUrl = ({ params }: { params: UserApiAuthGoogleSigninReq; }) =>
  get<UserApiAuthGoogleSigninRes>('/user/api/auth/google/signin', {
    params,
  });

/**
 * 获取 Facebook OAuth2 授权链接
 */
export const getFacebookSigninUrl = ({ params }: { params: UserApiAuthFacebookSigninReq; }) =>
  get<UserApiAuthFacebookSigninRes>('/user/api/auth/facebook/signin', {
    params,
  });

/**
 * 获取 Ethereum 登录的 Nonce
 */
export const getEthereumNonce = ({ params }: { params: UserApiAuthEthereumNonceReq; }) =>
  get<UserApiAuthEthereumNonceRes>('/user/api/auth/ethereum/nonce', {
    params,
  });

/**
 * 获取当前登录会话信息
 */
export const getAuthSession = ({ params }: { params: UserApiAuthSessionReq; }) =>
  get<UserApiAuthSessionRes>('/user/api/auth/session', {
    params,
  });

/**
 * 退出登录
 */
export const authSignout = ({ params }: { params: UserApiAuthSignoutReq; }) =>
  get<UserApiAuthSignoutRes>('/user/api/auth/signout', {
    params,
  });

/**
 * Ethereum 授权回调
 */
export const postCredentialsCallback = ({ params }: { params: UserApiAuthCredentialsCallbackReq; }) =>
  post<UserApiAuthCredentialsCallbackRes>('/user/api/auth/credentials/callback', params);

/**
 * Telegram Login Widget 回调验证
 */
export const postTelegramCallback = ({ params }: { params: UserApiAuthTelegramCallbackReq; }) =>
  post<UserApiAuthTelegramCallbackRes>('/user/api/auth/telegram/callback', params);

// ================ Hooks ================

/**
 * 获取 Google OAuth2 授权链接的 Hook
 */
export const useGetGoogleSigninUrl = createApiQuery(getGoogleSigninUrl, () => ['google-signin-url']);

/**
 * 获取 Facebook OAuth2 授权链接的 Hook
 */
export const useGetFacebookSigninUrl = createApiQuery(getFacebookSigninUrl, () => ['facebook-signin-url']);

/**
 * 获取 Ethereum 登录的 Nonce 的 Hook
 */
export const useGetEthereumNonce = createApiQuery(
  getEthereumNonce,
  (params) => ['ethereum-nonce', params.params.address],
);

/**
 * 获取当前登录会话信息的 Hook
 */
export const useGetAuthSession = createApiQuery(getAuthSession, () => ['auth-session']);

/**
 * 退出登录的 Hook
 */
export const useAuthSignout = createApiMutationHook(authSignout);

/**
 * Ethereum 授权回调的 Hook
 */
export const usePostCredentialsCallback = createApiMutationHook(postCredentialsCallback);

/**
 * Telegram Login Widget 回调验证的 Hook
 */
export const usePostTelegramCallback = createApiMutationHook(postTelegramCallback);
